import axios from "axios";
import camelcaseKeys from "camelcase-keys";
import { get, set } from "lodash";
import snakecaseKeys from "snakecase-keys";

import history from "@/app/history";
import { SESSION_EXPIRED_MESSAGE } from "@/constants/errorMessages";
import { API_URL, HTML_TO_PDF_URL, SEARCH_URL } from "@/env";
import { getLogRocketSessionUrl } from "@/utils/logrocketSession";
import { parseApiError } from "@/utils/parseApiError";
import { getTabId } from "@/utils/tabId";
import {
  enqueueRequest,
  getIsRefreshing,
  handleLogout,
  isDefinitiveAuthFailure,
  processQueue,
  refreshAuthToken,
  setIsRefreshing,
} from "@/utils/tokenRefreshManager";

const onTrialEndedError = ({ status, errorCode }) => {
  if (status === 403 && errorCode === "trial_ended") {
    history.push("/trial-ended");
  }
};

// Stamp the active LogRocket replay URL onto API requests so backend (and the
// html-to-pdf service) logs can be correlated with the session replay, and so a
// failed PDF render can echo it back in its error envelope. Scoped to origins
// whose CORS allow-lists this custom header: the Django API (API_URL) and
// the PDF service (HTML_TO_PDF_URL). The search service
// does not allow it, so it stays excluded to avoid a failing preflight.
axios.interceptors.request.use((config) => {
  const lrUrl = getLogRocketSessionUrl();
  if (
    lrUrl &&
    typeof config.url === "string" &&
    (config.url.startsWith(API_URL) || config.url.startsWith(HTML_TO_PDF_URL))
  ) {
    config.headers = config.headers || {};
    config.headers["X-LogRocket-URL"] = lrUrl;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    onTrialEndedError({
      status: error?.response?.status,
      errorCode: error?.response?.data?.code,
    });

    // Skip refresh logic if this is the refresh token request itself
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (getIsRefreshing()) {
        // If already refreshing, queue this request
        return enqueueRequest()
          .then((token) => {
            // Ensure headers object exists before assigning Authorization
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `${token.token_type} ${token.access_token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      setIsRefreshing(true);

      try {
        console.log("%c Attempting to refresh token...", "color: blue");
        const newToken = await refreshAuthToken();
        console.log("%c Token refreshed successfully", "color: green");
        // Ensure headers object exists before assigning Authorization
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `${newToken.token_type} ${newToken.access_token}`;
        processQueue(null, newToken);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        if (isDefinitiveAuthFailure(refreshError)) {
          console.log("%c Token refresh failed, logging out...", "color: orange");

          const sessionData = { detail: SESSION_EXPIRED_MESSAGE, code: "session_expired" };
          if (error.response?.data) {
            error.response.data = sessionData;
          } else {
            error.response = { data: sessionData, status: 401 };
          }
          error.message = SESSION_EXPIRED_MESSAGE;
          error.parsed = parseApiError(error);

          setTimeout(() => {
            handleLogout();
          }, 100);
        }

        return Promise.reject(error);
      } finally {
        setIsRefreshing(false);
      }
    }

    // Retry idempotent GETs once on a transient 502. The shared ALB intermittently
    // returns a one-off 502 when it reuses a backend keepalive connection that
    // gunicorn already closed (steady-state race), or that a draining task closed
    // mid-deploy — both succeed on an immediate re-request. Scoped to GET so a
    // non-idempotent write is never silently re-sent; the flag guards against loops.
    if (
      error.response?.status === 502 &&
      originalRequest &&
      originalRequest.method?.toLowerCase() === "get" &&
      !originalRequest._retry502
    ) {
      originalRequest._retry502 = true;
      await new Promise((resolve) => setTimeout(resolve, 400));
      return axios(originalRequest);
    }

    error.parsed = parseApiError(error);
    return Promise.reject(error);
  }
);

export const createApi =
  (
    httpMethod,
    url,
    {
      headers = null,
      responseType = null,
      isUrlData = false,
      inputData = (payload) => payload,
      outputData = (response) => response,
    } = {}
  ) =>
  (auth = JSON.parse(window.localStorage.getItem("auth")), payload) => {
    const axiosOptions = {
      method: httpMethod,
      url: API_URL + url,
      responseType: responseType || "json",
      headers: headers || {
        Accept: "application/json",
      },
      transformResponse: [outputData],
    };

    if (auth) {
      axiosOptions.headers.Authorization = `${auth.token_type} ${auth.access_token}`;
    }

    // Tag the client platform + per-tab id for the backend live-metrics middleware.
    axiosOptions.headers["X-Client-Platform"] = "web";
    axiosOptions.headers["X-Tab-Id"] = getTabId();

    if (httpMethod === "GET" || isUrlData || payload === undefined) {
      axiosOptions.params = inputData(payload);
    } else {
      axiosOptions.data = inputData(snakecaseKeys(payload));
    }

    return axios(axiosOptions);
  };

const getCancelToken = (cancelTokenId) => {
  return new axios.CancelToken((c) => {
    const cancel = c;
    const cancelationTokens = get(window, "cancelationTokens", {});
    cancelationTokens[cancelTokenId] = cancel;
    set(window, "cancelationTokens", cancelationTokens);
  });
};

export const cancelOngoingApiRequests = () => {
  const cancelationTokens = get(window, "cancelationTokens", {});
  Object.keys(cancelationTokens).forEach((cancel) => {
    cancelationTokens[cancel]();
  });
  set(window, "cancelationTokens", {});
};

export const createApiV2 =
  (
    httpMethod,
    url,
    {
      headers = null,
      responseType = null,
      isUrlData = false,
      preventOutputTransform = false,
      inputData = (payload) => payload,
    } = {}
  ) =>
  (auth = JSON.parse(window.localStorage.getItem("auth")), payload) => {
    const axiosOptions = {
      cancelToken: getCancelToken(url),
      method: httpMethod,
      url: `${API_URL}${url}`,
      responseType: responseType || "json",
      headers: {
        Accept: "application/json",
        // Tag the client platform + per-tab id for the backend live-metrics
        // middleware (both allow-listed in CORS_ALLOW_HEADERS).
        "X-Client-Platform": "web",
        "X-Tab-Id": getTabId(),
        ...(headers || {}),
      },
      transformResponse: (response) => {
        if (preventOutputTransform) {
          return response;
        }

        return typeof response === "object" && !!response
          ? camelcaseKeys(response, { deep: true })
          : response;
      },
    };

    if (auth) {
      axiosOptions.headers.Authorization = `${auth.token_type} ${auth.access_token}`;
    }

    if (httpMethod === "GET" || isUrlData || payload === undefined) {
      axiosOptions.params = inputData(payload);
    } else if (
      ["POST", "PATCH"].includes(httpMethod) &&
      headers &&
      headers["Content-Type"] === "multipart/form-data"
    ) {
      axiosOptions.data = inputData(payload);
    } else {
      axiosOptions.data = inputData(snakecaseKeys(payload));
    }

    return axios(axiosOptions);
  };

export const createApiPdf =
  (
    httpMethod,
    url,
    {
      headers = null,
      responseType = null,
      isUrlData = false,
      inputData = (payload) => payload,
      outputData = (response) => response,
    } = {}
  ) =>
  (auth = JSON.parse(window.localStorage.getItem("auth")), payload) => {
    const axiosOptions = {
      method: httpMethod,
      url: API_URL + url,
      responseType: responseType || "json",
      headers: headers || {
        Accept: "application/json",
      },
      transformResponse: [outputData],
    };

    if (auth) {
      axiosOptions.headers.Authorization = `${auth.token_type} ${auth.access_token}`;
    }

    if (httpMethod === "GET" || isUrlData || payload === undefined) {
      axiosOptions.params = inputData(payload);
    } else {
      axiosOptions.data = inputData(payload);
    }

    return axios(axiosOptions);
  };

export const createApiPdfV2 =
  (
    httpMethod,
    url,
    {
      headers = null,
      responseType = null,
      isUrlData = false,
      inputData = (payload) => payload,
    } = {}
  ) =>
  (auth = JSON.parse(window.localStorage.getItem("auth")), payload) => {
    const axiosOptions = {
      method: httpMethod,
      url: `${HTML_TO_PDF_URL}${url}`,
      responseType: responseType || "json",
      headers: headers || {
        Accept: "application/json",
      },
    };

    if (auth) {
      axiosOptions.headers.Authorization = `${auth.token_type} ${auth.access_token}`;
    }

    if (httpMethod === "GET" || isUrlData || payload === undefined) {
      axiosOptions.params = inputData(payload);
    } else {
      axiosOptions.data = inputData(payload);
    }

    return axios(axiosOptions);
  };

export const mockApi =
  (fakeData, timeout = 2000) =>
  () =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(fakeData);
      }, timeout);
    });

export const createSearchApi =
  (
    httpMethod,
    url,
    {
      headers = null,
      responseType = null,
      isUrlData = false,
      preventOutputTransform = false,
      inputData = (payload) => payload,
    } = {}
  ) =>
  (auth = JSON.parse(window.localStorage.getItem("auth")), payload) => {
    const axiosOptions = {
      cancelToken: getCancelToken(url),
      method: httpMethod,
      url: `${SEARCH_URL}${url}`,
      responseType: responseType || "json",
      headers: headers || {
        Accept: "application/json",
      },
      transformResponse: (response) => {
        if (preventOutputTransform) {
          return response;
        }

        let parsed = response;
        if (typeof parsed === "string") {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            return response;
          }
        }

        return parsed && typeof parsed === "object"
          ? camelcaseKeys(parsed, { deep: true })
          : parsed;
      },
    };

    if (auth) {
      axiosOptions.headers.Authorization = `${auth.token_type} ${auth.access_token}`;
    }

    if (httpMethod === "GET" || isUrlData || payload === undefined) {
      axiosOptions.params = inputData(payload);
    } else if (
      ["POST", "PATCH"].includes(httpMethod) &&
      headers &&
      headers["Content-Type"] === "multipart/form-data"
    ) {
      axiosOptions.data = inputData(payload);
    } else {
      axiosOptions.data = inputData(snakecaseKeys(payload));
    }

    return axios(axiosOptions);
  };
